import { useEffect, useState } from 'react';
import { AUTHORIZATION_ROLE_LABELS, ROLE_CATALOGUE } from '@scoutaround/script-breakdown-shared';

import { api, getToken, isWorkspaceRoleRequired } from '../lib/api.js';
import { CONTACT_URL, WorkspaceAccessRequired } from '../components/WorkspaceAccessRequired.js';
import { enterHref } from './EnterPage.js';

type Preview = Awaited<ReturnType<typeof api.previewInvitation>>;

/** What the catalogue says the role does, or null for one this build has never
 *  heard of — a newer deploy can mint a role an older SPA does not know. */
function summaryOf(role: string): string | null {
  return ROLE_CATALOGUE[role as keyof typeof ROLE_CATALOGUE]?.summary ?? null;
}

/**
 * The invitation link, opened.
 *
 * WORKS SIGNED OUT, and that is the whole point. A producer invites people by
 * email, and most of them are not ScoutAround users — a page behind the session
 * gate would show the sign-in wall to exactly the person the invitation was
 * for, with no way to tell what they were being invited to. So the preview is
 * unauthenticated and says which production and which role, before anybody is
 * asked to create anything.
 *
 * THE PREVIEW LEAKS NOTHING THE EMAIL DID NOT. It repeats what the message
 * already told the recipient and nothing else about the project, so a guessed
 * token is worth no more than a guessed token.
 */
export function InvitationPage({ token }: { token: string }) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  /** The API's sentence when accepting met 403 WORKSPACE_ROLE_REQUIRED. */
  const [noAccess, setNoAccess] = useState<string | null>(null);

  const signedIn = getToken() !== null;

  useEffect(() => {
    let live = true;

    api
      .previewInvitation(token)
      .then((result) => {
        if (live) setPreview(result);
      })
      .catch(() => {
        if (live) setLoadError('That invitation link is not valid.');
      });

    return () => {
      live = false;
    };
  }, [token]);

  function accept() {
    setAccepting(true);
    setAcceptError(null);
    setNoAccess(null);

    api
      .acceptInvitation(token)
      .then((result) => {
        // A FULL LOAD, not a router navigate — the same reason `EnterPage`
        // reloads. Membership has just changed, and every query on the way in
        // should ask again rather than render a cache taken before it.
        window.location.replace(`/projects/${result.projectId}/overview`);
      })
      .catch((error: unknown) => {
        setAccepting(false);

        // NOT A FAILURE TO RETRY. The account has no Production workspace
        // access, and accepting cannot grant it (ADR 0010) — only ScoutAround
        // can. "Try the link again" sent people round that loop forever.
        if (isWorkspaceRoleRequired(error)) {
          setNoAccess(error.message);
          return;
        }

        setAcceptError('We could not add you to this production. Try the link again.');
      });
  }

  return (
    <main
      data-testid="invitation"
      style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div className="card1" style={{ width: 520, maxWidth: '100%', padding: 32 }}>
        <p className="kick">Production workspace</p>

        {loadError !== null ? (
          <Dead testId="invitation-invalid" heading="This link is not valid">
            {loadError} Ask whoever invited you to send a new one.
          </Dead>
        ) : preview === null ? (
          <p className="dh" style={{ fontSize: 24, marginTop: 10 }} data-testid="invitation-loading">
            Checking that invitation…
          </p>
        ) : preview.state === 'EXPIRED' ? (
          <Dead testId="invitation-expired" heading="This invitation has expired">
            Invitations are valid for fourteen days. Ask for a new one and it will work straight
            away.
          </Dead>
        ) : preview.state === 'REVOKED' ? (
          <Dead testId="invitation-revoked" heading="This invitation was withdrawn">
            The production team cancelled it. If that looks wrong, speak to whoever invited you.
          </Dead>
        ) : preview.state === 'ACCEPTED' && !signedIn ? (
          // Deliberately not "you are already a member": we do not know who is
          // holding this link. It says the invitation is spent, and points at
          // the one place that can tell them anything more.
          <Dead testId="invitation-used" heading="This invitation has already been used">
            Sign in on ScoutAround and open the Production workspace from your profile menu. If it
            is not there, ScoutAround has not enabled it for your account yet — ask through the{' '}
            <a href={CONTACT_URL} data-testid="invitation-used-contact">
              contact page
            </a>
            .
          </Dead>
        ) : (
          <>
            <h1 className="dh" style={{ fontSize: 28, marginTop: 10 }}>
              Join {preview.projectTitle}
            </h1>

            <p
              className="mm"
              style={{ fontSize: 13, marginTop: 12, lineHeight: 1.6 }}
              data-testid="invitation-terms"
            >
              You have been invited to this production as{' '}
              {/* The LABEL, never the row. `assistant_location_manager` is a
                  database value, and this sentence is read by somebody who has
                  just clicked a link in an email. */}
              <strong>
                {AUTHORIZATION_ROLE_LABELS[
                  preview.authorizationRole as keyof typeof AUTHORIZATION_ROLE_LABELS
                ] ?? preview.authorizationRole.toLowerCase()}
              </strong>
              {preview.productionTitle !== null && preview.productionTitle !== ''
                ? `, credited as ${preview.productionTitle}`
                : ''}
              . The invitation was sent to {preview.email}.
            </p>

            {/* WHAT THE ROLE ACTUALLY DOES, at the one moment somebody reads
                carefully. The page named the role and stopped there, which
                tells a location scout nothing they did not already know and
                tells a script supervisor nothing at all. This is the catalogue
                summary — the production may have narrowed or widened it, and
                that is visible on the Team tab once they are in; promising the
                exact set here would mean fetching a project they are not yet a
                member of. */}
            {summaryOf(preview.authorizationRole) === null ? null : (
              <p
                className="mm"
                data-testid="invitation-role-summary"
                style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.6 }}
              >
                {summaryOf(preview.authorizationRole)} This production may have adjusted it — you will see
                exactly what applies under Team.
              </p>
            )}

            {/* A STATUS, announced: it replaces the Join button the person just
                pressed. The live region is mounted, empty, for as long as the
                person is signed in and only its CONTENT changes — a region that
                appears already populated is not announced by many screen
                readers. */}
            {signedIn ? (
              <div role="status" aria-live="polite" data-testid="invitation-no-access-status">
                {noAccess !== null ? (
                  <div style={{ marginTop: 18 }}>
                    <WorkspaceAccessRequired testId="invitation-no-access" message={noAccess} />
                    <p
                      className="mm"
                      style={{ fontSize: 12, marginTop: 12, lineHeight: 1.6 }}
                      data-testid="invitation-no-access-stays-open"
                    >
                      The invitation stays open until it expires. Once ScoutAround has enabled your
                      account, open this link again to join.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}

            {signedIn && noAccess !== null ? null : signedIn ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginTop: 18 }}
                  data-testid="invitation-accept"
                  disabled={accepting}
                  onClick={accept}
                >
                  {accepting ? 'Joining…' : 'Join this production'}
                </button>
                {acceptError !== null ? (
                  <p
                    className="mm"
                    style={{ fontSize: 12, marginTop: 10, color: 'var(--danger)' }}
                    data-testid="invitation-accept-error"
                  >
                    {acceptError}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <a
                  className="btn btn-primary"
                  style={{ marginTop: 18 }}
                  data-testid="invitation-signin"
                  href={enterHref(`/invitations/${token}`)}
                >
                  Sign in or create an account
                </a>
                <p className="mm" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.6 }}>
                  {/* The address is not a credential — acceptance is by token
                      (see `TeamService.acceptInvitation`) — so a different
                      account is fine, and saying so stops people abandoning the
                      flow because the invited address is a work account they do
                      not use. What it must NOT promise is access: the account
                      also needs the Production workspace, which ScoutAround
                      enables and accepting never grants (ADR 0010). Without it,
                      signing in ends on the client site's no-access page, not
                      back here. */}
                  Joining needs a ScoutAround account with the Production workspace enabled — it
                  does not have to use the address above. ScoutAround enables the workspace account
                  by account: if yours is enabled, sign in and you will come back here. If not, or
                  if you are new to ScoutAround, ask us through the{' '}
                  <a href={CONTACT_URL} data-testid="invitation-contact">
                    contact page
                  </a>{' '}
                  first.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}

function Dead({
  testId,
  heading,
  children,
}: {
  testId: string;
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <h1 className="dh" style={{ fontSize: 26, marginTop: 10 }}>
        {heading}
      </h1>
      <p className="mm" style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6 }} data-testid={testId}>
        {children}
      </p>
    </>
  );
}
